<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.price_vendor_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                column="1"
                @click="openAddVendorPrice">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>

        <hts-data-table
            v-if="checkPermission(layoutPermissions.price_vendor_management)"
            ref="dataTables"
            :headers="headers"
            :server="apiVendorPrices"
            :use-archive="usePriceVendorArchive"
            use-selection
            store-name="priceDataTable"
            store-key="PriceVendor" />
        <add-vendor-price
            ref="addVendorPrice"
            @refresh-tables="refreshTables()" />

        <detail-vendor-price ref="detailVendorPrice" />
        <hts-modal
            v-if="checkPermission(layoutPermissions.price_vendor_delete)"
            :show="deleteVendorPrice"
            :title="$t('labels.vendorPriceModule.delete')"
            type="confirm"
            :content="$t('labels.vendorPriceModule.confirmDelete')"
            @hide="closeDeleteVendorPrice"
            @confirm="() => actionDeleteVendorPrice()" />

        <router-view @refresh-tables="refreshTables" />
    </div>
</template>
<script setup lang="ts">
import AddVendorPrice from './vendor/-components/add.vue'
import Action from './vendor/action.vue'
import DetailVendorPrice from './vendor/detail.vue'
import { IVendorPrice, numberFormatter } from './vendor/ignore/data'
import Status from './vendor/status.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { $api, $lodash, $url } from '#imports'

const { $formatters } = useNuxtApp()
const route = useRoute()

const addVendorPrice = ref()
const deleteVendorPrice = ref()
const detailVendorPrice = ref()
const editVendorPrice = ref()
const priceVendorSelectedForDelete = ref<String>('')
const dataTables = ref()
const apiVendorPrices = $url.price.vendor.gets
const vehicleCategories = ref<null | Array<any>>()
const openAddVendorPrice = () => {
  if (addVendorPrice.value) {
    addVendorPrice.value.open()
  }
}

onBeforeMount(() => {
  const api = $urlAdmin.vehicle.category.getAll

  $api(api).then((data) => {
    const resData: any = data?.data?.value

    if (resData.success) {
      vehicleCategories.value = resData.data || []
    }
  })
})

onMounted(() => {
  const action: string | null = String(route.query.action) || null
  const id: string | null = String(route.query.id) || null
  const by: string | null = String(route.query.by) || null
  if (action === 'create' && id && by) {
    addVendorPrice.value.open(id, by)
  }
})

watch(
  () => route.query,
  () => {
    const action: string | null = String(route.query.action) || null
    const id: string | null = String(route.query.id) || null
    const by: string | null = String(route.query.by) || null
    if (action === 'create' && id && by) {
      addVendorPrice.value.open(id, by)
    }
  }
)

const openDelete = (priceVendor: IVendorPrice) => {
  priceVendorSelectedForDelete.value = priceVendor._id || ''
  deleteVendorPrice.value = true
}

const closeDeleteVendorPrice = () => {
  priceVendorSelectedForDelete.value = ''
  deleteVendorPrice.value = false
}

const openViewDetail = (priceVendor: IVendorPrice) => {
  if (detailVendorPrice?.value) {
    detailVendorPrice.value.open(priceVendor._id)
  }
}

const openEdit = (priceVendor: IVendorPrice) => {
  if (priceVendor) {
    useRouter().push({
      name: 'trucker-id-price-vendor-cooperatorId-edit',
      params: { cooperatorId: priceVendor._id as string }
    })
  }
}

const openView = (priceVendor: IVendorPrice) => {
  if (priceVendor) {
    useRouter().push({
      name: 'trucker-id-price-vendor-cooperatorId-view',
      params: { cooperatorId: priceVendor._id as string }
    })
  }
}

const actionDeleteVendorPrice = $lodash.debounce(() => {
  const api = $url.price.vendor.delete

  $api(api, { body: { ids: [priceVendorSelectedForDelete.value] } }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      priceVendorSelectedForDelete.value = ''
      refreshTables()
      deleteVendorPrice.value = false
      process.client && $toast().success($t('labels.deleteSuccess'))
    }
  })
}, 300)

const headers = [
  {
    field: 'vendor',
    headerName: $t('labels.nameFleetVehicle'),
    minWidth: 250,
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      return params.data?.vendor?.name || ''
    }
  },
  {
    field: 'positions',
    headerName: $t('labels.route'),
    filter: 'agTextColumnFilter',
    minWidth: 500,
    valueFormatter: (params: any) => {
      if (params.data?.positions && Array.isArray(params.data?.positions)) {
        const pluckData = $lodash.map(params.data?.positions, 'fullNameNode')

        return $lodash.join(pluckData, ' >> ')
      }
    }
  },
  {
    field: 'price',
    headerName: $t('labels.price'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      return params.data.price ? numberFormatter(params.data.price) : ''
    }
  },
  {
    field: 'workflowName',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.workflow')
  },
  {
    field: 'typeCommodity',
    headerName: $t('labels.typeCommodity'),
    valueGetter: (params: any) => {
      const result: Array<string> = []
      if (params.data.typeCommodity && Array.isArray(params.data.typeCommodity)) {
        params.data.typeCommodity.forEach((item: string) => {
          if (item === 'NORMAL') {
            result.push($t('labels.ordinaryGoods'))
          } else if (item === 'FROZEN') {
            result.push($t('labels.frozen'))
          } else if (item === 'DANGEROUS') {
            result.push($t('labels.dangerousGoods'))
          } else if (item === 'FRAGILE') {
            result.push($t('labels.fragileGoods'))
          } else if (item === 'LIQUID') {
            result.push($t('labels.liquid'))
          } else if (item === 'GAS') {
            result.push($t('labels.gas'))
          } else if (item === 'OVERWEIGHT') {
            result.push($t('labels.overweightCargo'))
          }
        })
      }
      return result.join(',')
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: $t('labels.notActiveYet'), value: 'notActiveYet', class: { '!bg-yellow-100': true, '!text-yellow-600': true } }, // Not Active Yet: Màu vàng
      { name: $t('labels.active'), value: 'active', class: { '!bg-green-100': true, '!text-green-600': true } }, // Active: Màu xanh lá cây
      { name: $t('labels.expired'), value: 'expired', class: { '!bg-red-100': true, '!text-red-600': true } } // Expired: Màu đỏ
    ],
    filterModelDefault: { type: 'equals', filterType: 'text' }
  },
  {
    field: 'oilPrice',
    headerName: $t('labels.oilPrice'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      return params.data.oilPrice ? numberFormatter(params.data.oilPrice) : ''
    }
  },
  {
    field: 'vehicleCategory',
    headerName: $t('labels.vehicleType'),
    valueFormatter: (params: any) => {
      if (params.data?.transportation === 'NON_CONTAINER' && params.data?.vehicleCategoryName) {
        return params.data?.vehicleCategoryName
      }
      return ''
    }
  },
  {
    field: 'contCategory',
    headerName: $t('labels.containerType'),
    valueFormatter: (params: any) => {
      if (params.data?.transportation === 'CONTAINER' && params.data?.contCategory) {
        if (params.data?.contCategory === 20) {
          return $t('labels.20f')
        }
        if (params.data?.contCategory === 40) {
          return $t('labels.40f')
        }
        if (params.data?.contCategory === 45) {
          return $t('labels.45f')
        }
      }
      return ''
    }
  },
  {
    field: 'priceOfflineFee',
    headerName: $t('labels.type'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data.priceOfflineFee) {
        return $t('labels.pricesFromOfflineFees')
      }
      return $t('labels.priceNode')
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Action,
    cellRendererParams: {
      storeKey: 'priceDataTable',
      storeName: 'PriceVendor',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.price_vendor_edit),
          click: openEdit
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.price_vendor_management),
          click: openView,
          isAll: true
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-500 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.price_vendor_delete),
          click: openDelete,
          isArchive: true
        }
      ]
    }
  }
]

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const usePriceVendorArchive = computed(() => {
  return {
    api: $url.price.vendor.archive,
    permissions: checkPermission(layoutPermissions.price_vendor_archive)
  }
})
</script>
